import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import { LINEAGE_COLOR_BY_LEGEND } from './constants';
import { useLineageGraphContext } from './LineageGraphContext';
import type { LineageNodeColors } from './types';
import {
  getFilterHighlightCss,
  getLineageCategoryColors,
} from './utils/lineageDisplaySettings';

const EXPAND_DELAY_MS = 150;

const Wrapper = styled(FlexColumn)`
  justify-content: center;
  background: ${({ theme }) => theme.palette.common.white};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray13};
  outline: none;
`;

const ColorByEntries = styled(FlexColumn)`
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray13};
`;

const LegendChip = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const ChipIcon = styled.div<{ $colors: LineageNodeColors }>`
  height: 10px;
  width: 10px;
  border-radius: 100%;
  border: 1px solid ${({ $colors }) => $colors.border};
  background: ${({ $colors }) => $colors.background};
`;

const FilteredChipIcon = styled.div`
  height: 10px;
  width: 10px;
  border-radius: 100%;
  border: 1px solid ${({ theme }) => theme.palette.common.gray18};
  background: ${({ theme }) => theme.palette.common.white};
  ${({ theme }) => getFilterHighlightCss(theme.palette, { scale: 0.5 })}
`;

type Props = {
  className?: string;
  /** Label for the highlighted/selected nodes legend entry. Defaults to "Filtered". */
  selectedLabel?: string;
  /** Shows only the selected entry until hovered or focused, for small embedded graphs. */
  isCompact?: boolean;
};

const Legend = ({
  className,
  selectedLabel = 'Filtered',
  isCompact = false,
}: Props) => {
  const theme = useTheme();
  const { displaySettings } = useLineageGraphContext();
  const colorByEntries = LINEAGE_COLOR_BY_LEGEND[displaySettings.colorBy];
  const [isExpanded, setIsExpanded] = useState(false);
  const expandTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isOpen = !isCompact || isExpanded;

  useEffect(() => () => clearTimeout(expandTimeoutRef.current), []);

  const expand = (delayMs: number) => {
    clearTimeout(expandTimeoutRef.current);
    expandTimeoutRef.current = setTimeout(() => setIsExpanded(true), delayMs);
  };

  const collapse = () => {
    clearTimeout(expandTimeoutRef.current);
    setIsExpanded(false);
  };

  const expandHandlers = isCompact
    ? {
        role: 'group',
        'aria-label': 'Legend',
        tabIndex: 0,
        onMouseEnter: () => expand(EXPAND_DELAY_MS),
        onMouseLeave: collapse,
        onFocus: () => expand(0),
        onBlur: collapse,
      }
    : {};

  return (
    <Wrapper className={className} {...expandHandlers}>
      {colorByEntries.length > 0 && (
        <Collapse in={isOpen}>
          <Box
            sx={{
              pb: '12px',
              opacity: isOpen ? 1 : 0,
              transition: theme.transitions.create('opacity'),
            }}
          >
            <ColorByEntries>
              {colorByEntries.map(({ category, label }) => (
                <LegendChip key={category}>
                  <ChipIcon
                    $colors={getLineageCategoryColors(theme.palette, category)}
                  />
                  <Typography>{label}</Typography>
                </LegendChip>
              ))}
            </ColorByEntries>
          </Box>
        </Collapse>
      )}
      <LegendChip>
        <FilteredChipIcon />
        <Typography>{selectedLabel}</Typography>
      </LegendChip>
    </Wrapper>
  );
};

export default Legend;
